import {
  Add,
  ArrowForward,
  DeleteOutline,
  DescriptionOutlined,
  Launch,
  Search,
  ErrorOutline,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { useEffect, useMemo, useState } from "react";
import ReportFilters from "../../../componentes/easyReport/reportFilters";
import { useNavigate } from "react-router-dom";
import * as dayjs from "dayjs";
import { useStyle } from "./styles";
import AlertModal from "../../../componentes/alertModal";
import api from "../../../services/api";
import { ptBR } from "@mui/x-data-grid/locales";
import { notifyOnPage } from "../../../utils/notifyOnPage";
import { ReactComponent as EmptyBox } from "../../../assets/easyReport/iconSvg/emptyBox.svg";
import { socketCliente } from "../../../services/socket";

interface Relatorio {
  id: string;
  idGenerated: string;
  titulo: string;
  perfilRelatorio: string[];
  dataInicial: string;
  dataFinal: string;
  hosts: string[];
  geradoEm: string;
  visualizar?: string;
  loading?: boolean;
  progress?: number | null;
}

export default function Relatorios() {
  const [relatorios, setRelatorios] = useState<Relatorio[]>([]);
  const [relatorioToDelete, setRelatorioToDelete] = useState<string[] | null>(
    null,
  );
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>("");
  const [filtroDataInicial, setFiltroDataInicial] =
    useState<dayjs.Dayjs | null>(null);
  const [filtroDataFinal, setFiltroDataFinal] = useState<dayjs.Dayjs | null>(
    null,
  );
  const [filtroGeradoEm, setFiltroGeradoEm] = useState<dayjs.Dayjs | null>(
    null,
  );
  const [pageSize, setPageSize] = useState<number>(10);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const theme = useTheme();
  const styles = useStyle(theme);

  useEffect(() => {
    const fetchReports = async () => {
      try {
        setLoading(true);
        const res = await api.get("/easyReport/v2/findManyReports");

        const sortedRes = res.data.sort((a: any, b: any) => {
          return (
            new Date(b.geradoEm).getTime() - new Date(a.geradoEm).getTime()
          );
        });

        setRelatorios(sortedRes);
      } catch (error) {
        console.error("Erro ao buscar relatórios:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchReports();
  }, []);

  const handleDownload = async (id: string, titulo: string) => {
    try {
      const response = await api.post(
        "/easyReport/v2/downloadReport",
        { id },
        { responseType: "blob" },
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${titulo || "relatorio"}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Erro ao baixar relatório:", error);
    }
  };

  useEffect(() => {
    const loadingReports = relatorios.filter((r) => r.loading);

    loadingReports.forEach((report) => {
      if (report.id.startsWith("report_progress_")) {
        const handleProgress = (data: {
          jobId: string;
          percentage: number;
        }) => {
          if (data.percentage >= 100) {
            const cleanId = data.jobId.replace("report_progress_", "");
            handleDownload(cleanId, report.titulo);

            setRelatorios((prev) =>
              prev.map((r) =>
                r.id === data.jobId
                  ? { ...r, id: cleanId, progress: 100, loading: false }
                  : r,
              ),
            );
          } else {
            setRelatorios((prev) =>
              prev.map((r) =>
                r.id === data.jobId ? { ...r, progress: data.percentage } : r,
              ),
            );
          }
        };

        socketCliente.off(report.id, handleProgress);
        socketCliente.on(report.id, handleProgress);
      }
    });

    return () => {
      loadingReports.forEach((report) => {
        if (report.id.startsWith("report_progress_")) {
          socketCliente.off(report.id);
        }
      });
    };
  }, [relatorios]);

  const columns: GridColDef<Relatorio>[] = [
    {
      field: "titulo",
      headerName: "Título do relatório",
      width: 100,
      flex: 1,
    },
    {
      field: "perfilRelatorio",
      headerName: "Perfil de Relatório",
      sortable: false,
      width: 150,
      flex: 1,
      renderCell: (params: GridRenderCellParams<Relatorio, string[]>) => (
        <Box sx={styles.boxRenderCell}>
          {Array.isArray(params.value) &&
            params.value.map((perfil) => (
              <Tooltip title={perfil}>
                <Chip key={perfil} label={perfil} sx={styles.chip} />
              </Tooltip>
            ))}
        </Box>
      ),
    },
    {
      field: "hosts",
      headerName: "Hosts",
      minWidth: 250,
      flex: 2,
      sortable: false,
      renderCell: (params: GridRenderCellParams<Relatorio, string[]>) => {
        const value = params.value || [];
        const display = value.join(", ");
        return (
          <Tooltip title={display}>
            <Box
              sx={{
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
                width: "100%",
              }}
            >
              {display}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: "intervaloColeta",
      headerName: "Intervalo de Coleta",
      sortable: false,
      width: 240,
      renderCell: (params: GridRenderCellParams<Relatorio>) => (
        <Box sx={styles.boxRenderCell}>
          <Typography sx={styles.typography}>
            {"" +
              dayjs(new Date(params.row.dataInicial))
                .toDate()
                .toLocaleDateString() || ""}
          </Typography>
          <ArrowForward sx={{ fontSize: "1.1rem", paddingBottom: "2px" }} />
          <Typography sx={styles.typography}>
            {"" +
              dayjs(new Date(params.row.dataFinal))
                .toDate()
                .toLocaleDateString() || ""}
          </Typography>
        </Box>
      ),
    },
    {
      field: "geradoEm",
      headerName: "Gerado Em",
      width: 130,
      renderCell: (params: GridRenderCellParams<Relatorio, string>) =>
        params.value && (
          <Stack sx={styles.containerColumnLastLogin}>
            <Typography fontSize={13} sx={styles.textDate}>
              {dayjs(params.value).locale("pt-br").format("D [de] MMM, YYYY")}
            </Typography>
          </Stack>
        ),
    },
    {
      field: "options",
      headerName: "Ações",
      width: 100,
      minWidth: 100,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (params: GridRenderCellParams<Relatorio>) => {
        if (params.row.loading) {
          if (
            params.row.progress === null ||
            params.row.progress === undefined
          ) {
            return (
              <Box
                sx={{ display: "flex", gap: 1, alignItems: "center", ml: -1 }}
              >
                <Tooltip title="Criar a partir deste">
                  <IconButton
                    onClick={(e) => {
                      handleCreateUsingThis(params.row.id);
                    }}
                  >
                    <Launch fontSize="medium" color="primary" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Erro ao gerar relatório">
                  <ErrorOutline fontSize="medium" color="error" />
                </Tooltip>
              </Box>
            );
          }

          return (
            <Tooltip title={`Gerando... ${Math.round(params.row.progress)}%`}>
              <Box sx={{ position: "relative", display: "inline-flex" }}>
                <CircularProgress
                  variant="determinate"
                  value={params.row.progress}
                  size={24}
                />
              </Box>
            </Tooltip>
          );
        }

        const handleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
          e.stopPropagation();
          try {
            const response = await api.post(
              "/easyReport/v2/downloadReport",
              { id: params.row.id },
              { responseType: "blob" },
            );
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute(
              "download",
              `${params.row.titulo || "relatorio"}.pdf`,
            );
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
          } catch (error) {
            console.error("Erro ao baixar relatório:", error);
          }
        };
        return (
          <>
            <Tooltip title="Criar a partir deste">
              <IconButton
                onClick={(e) => {
                  handleCreateUsingThis(params.row.id);
                }}
              >
                <Launch fontSize="medium" color="primary" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Visualizar">
              <IconButton onClick={handleClick}>
                <DescriptionOutlined fontSize="medium" color="primary" />
              </IconButton>
            </Tooltip>
          </>
        );
      },
    },
  ];

  const handleClickNewReport = () => {
    navigate("/easyReportV2/novoRelatorio");
  };

  const handleDeleteModal = async () => {
    if (!relatorioToDelete || relatorioToDelete.length === 0) return;
    const id = relatorioToDelete;
    try {
      const logsString = localStorage.getItem("logs");
      const logsLoginClienteId = logsString ? JSON.parse(logsString)?.id : "";
      await api.post("/easyReport/v2/deleteReport", {
        id,
        logsLoginClienteId,
      });
      setRelatorios((prev) => prev.filter((r) => !id.includes(r.id)));
      setRelatorioToDelete(null);
      setDeleteModal(false);
      notifyOnPage(
        "easyReportV2/relatorios",
        "Relatórios excluídos com sucesso!",
        "success",
        3000,
      );
    } catch (error) {
      setDeleteModal(false);
      notifyOnPage(
        "easyReportV2/relatorios",
        (error as any)?.response?.data?.error || "Erro ao deletar itens",
        "error",
        3000,
      );
    }
  };

  const handleCreateUsingThis = (id: string) => {
    navigate(`/easyReportV2/novoRelatorio/${id}`);
  };

  const filteredRows = useMemo(() => {
    return relatorios.filter((row) => {
      const search = searchValue.toLowerCase();
      const matchesSearch =
        row.titulo.toLowerCase().includes(search) ||
        row.hosts.some((item) => item.toLowerCase().includes(search));

      const rowDataInicial = dayjs(row.geradoEm);
      const rowDataFinal = dayjs(row.geradoEm);
      const rowGeradoEM = dayjs(row.geradoEm);

      const matchesDataInicial = filtroDataInicial
        ? rowDataInicial.isSameOrAfter(filtroDataInicial, "day")
        : true;
      const matchesDataFinal = filtroDataFinal
        ? rowDataFinal.isSameOrBefore(filtroDataFinal, "day")
        : true;
      const matchesGeradoEm = filtroGeradoEm
        ? rowGeradoEM.isSame(filtroGeradoEm, "day")
        : true;

      return (
        matchesSearch &&
        matchesDataInicial &&
        matchesDataFinal &&
        matchesGeradoEm
      );
    });
  }, [
    searchValue,
    filtroDataInicial,
    filtroDataFinal,
    filtroGeradoEm,
    relatorios,
  ]);

  function CustomNoRowsOverlay() {
    return (
      <Box sx={styles.boxNoRowsOverlay}>
        <EmptyBox style={styles.iconUserNotFound} />
        <Typography color="text.disabled">
          Nenhum relatório até o momento
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={styles.container}>
      <Typography variant="h6">Relatórios</Typography>
      <Paper sx={styles.paper}>
        <Box sx={styles.boxHeader}>
          <Box sx={styles.boxSearchAndFilter}>
            <TextField
              size="small"
              value={searchValue}
              placeholder="Pesquisar por título, host, ou item"
              onChange={(e) => setSearchValue(e.target.value)}
              sx={styles.searchInput}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <Search />
                    </InputAdornment>
                  ),
                },
              }}
            />
            <ReportFilters
              filtroDataFinal={filtroDataFinal}
              setFiltroDataFinal={setFiltroDataFinal}
              filtroDataInicial={filtroDataInicial}
              setFiltroDataInicial={setFiltroDataInicial}
              filtroGeradoEm={filtroGeradoEm}
              setFiltroGeradoEm={setFiltroGeradoEm}
            />
          </Box>
          <Box sx={styles.boxButton}>
            <Button
              disabled={
                relatorioToDelete === null || relatorioToDelete?.length === 0
              }
              startIcon={<DeleteOutline />}
              color="error"
              sx={styles.button}
              onClick={() => setDeleteModal(true)}
            >
              Deletar Relatório
            </Button>
            <Button
              startIcon={<Add />}
              sx={styles.button}
              variant="contained"
              onClick={handleClickNewReport}
            >
              Novo Relatório
            </Button>
          </Box>
        </Box>
        <Box sx={{ width: "100%", height: "67vh", overflow: "auto" }}>
          <DataGrid
            sx={{
              ...(styles.dataGrid as any),
              "& .loading-row": {
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 123, 0, 0.2) !important"
                    : "rgba(224, 108, 0, 0.4) !important",
                "&:hover": {
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255, 123, 0, 0.3) !important"
                      : "rgba(224, 108, 0, 0.5) !important",
                },
              },
            }}
            rows={filteredRows}
            columns={columns}
            onRowSelectionModelChange={(e) => {
              setRelatorioToDelete(e as string[]);
            }}
            loading={loading}
            disableColumnMenu={true}
            pageSizeOptions={[10, 25, 50, 100]}
            onPaginationModelChange={(model) => setPageSize(model.pageSize)}
            checkboxSelection
            disableRowSelectionOnClick
            localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
            getRowClassName={(params) =>
              params.row.loading && params.row.progress != null
                ? "loading-row"
                : params.indexRelativeToCurrentPage % 2 === 0
                  ? "even-row"
                  : "odd-row"
            }
            slots={{
              noRowsOverlay: CustomNoRowsOverlay,
            }}
            initialState={{
              pagination: {
                paginationModel: { pageSize: pageSize },
              },
            }}
          />
        </Box>
        <AlertModal
          openModal={deleteModal}
          setOpenModal={setDeleteModal}
          severity="delete"
          singleButton={false}
          textContent={
            "Se prosseguir com a operação, o relatório será movido para a lixeira e excluído permanentemente após 30 dias."
          }
          handleButton={handleDeleteModal}
          handleCancel={() => {
            setDeleteModal(false);
          }}
        />
      </Paper>
    </Box>
  );
}
